import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { consumeAuthFromLocation } from "../utils/authToken";

const AgentPrivateRoute = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 15; // Try up to 15 times (3 seconds total)
    
    const authReturn = consumeAuthFromLocation(
      location.pathname,
      location.search,
    );
    if (authReturn.shouldReplace) {
      const newUrl = authReturn.nextSearch
        ? `${authReturn.nextPath}?${authReturn.nextSearch}`
        : authReturn.nextPath;
      navigate(newUrl, { replace: true });
    }
    
    const checkAuthorization = () => {
      const localdata = localStorage.getItem("sundorbonBookingUserDetails");
      
      // If no data and we haven't exhausted retries, try again
      if (!localdata && retryCount < maxRetries) {
        retryCount++;
        setTimeout(checkAuthorization, 200); // Wait 200ms and retry
        return;
      }
      
      if (!localdata) {
        console.warn("AgentPrivateRoute - No user details found after retries");
        toast.error("Please login to access this page.", {
          position: "top-right",
          autoClose: 3000,
        });
        navigate("/");
        setIsChecking(false);
        return;
      }

      try {
        const localStorageObj = JSON.parse(localdata);
        const userRole = localStorageObj?.Type;
        
        // Debug logging
        console.log("AgentPrivateRoute - User Role:", userRole);
        console.log("AgentPrivateRoute - Full user details:", localStorageObj);

        // Check if role is Agent (case-insensitive)
        const normalizedRole = userRole ? userRole.toString().trim() : "";
        if (normalizedRole.toLowerCase() === "agent" || normalizedRole === "Agent") {
          console.log("AgentPrivateRoute - Authorization granted");
          setIsAuthorized(true);
        } else {
          console.warn("AgentPrivateRoute - Access denied. User role:", userRole, "Expected: Agent");
          toast.error(`Access Denied. Agent role required. Current role: ${userRole || "None"}`, {
            position: "top-right",
            autoClose: 5000,
          });
          navigate("/");
        }
      } catch (error) {
        console.error("Error parsing user details:", error);
        toast.error("Session expired. Please login again.", {
          position: "top-right",
          autoClose: 3000,
        });
        navigate("/");
      } finally {
        setIsChecking(false);
      }
    };

    // Start checking immediately, then retry if needed
    checkAuthorization();
  }, [navigate, location.search, location.pathname]);

  if (isChecking) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg">Checking authorization...</div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="flex justify-center items-center min-h-screen flex-col gap-3">
        <h1 className="text-4xl font-semibold text-red-600">Access Denied</h1>
        <p className="text-xl text-gray-600">You do not have permission to access this page.</p>
        <p className="text-lg text-gray-500">Agent role required.</p>
        <button 
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" 
          onClick={() => navigate("/")}
        >
          Go to Home
        </button>
      </div>
    );
  }

  return <>{children}</>;
};

export default AgentPrivateRoute;

